import { useEffect, useRef, useState } from 'react'
import { BackendRemote } from '../backend-com'
import { useRpcFetch } from './useFetch'
import { reorderedAccounts } from '../utils/accountOrder'

export interface DropIndicator {
  index: number
  position: 'top' | 'bottom'
}

export interface UseAccountDragAndDropReturn {
  draggedAccountId: number | null
  dropIndicator: DropIndicator | null
  handleDragStart: (e: React.DragEvent, accountId: number) => void
  handleDragOver: (e: React.DragEvent, index: number) => void
  handleDragLeave: () => void
  handleDragEnd: () => void
  handleDrop: (e: React.DragEvent) => void
  handleTouchStart: (e: React.TouchEvent, accountId: number) => void
}

/**
 * How long the finger has to rest on a profile before the press becomes a
 * reorder. Shorter than the ~500ms after which the browser fires
 * `contextmenu`, so the two gestures can't both claim the same press.
 */
const ARM_DELAY_MS = 250
/** Movement below this is the finger being imprecise, not a gesture. */
const SLOP_PX = 8

export function useAccountDragAndDrop(
  accountsFetch: NonNullable<ReturnType<typeof useRpcFetch>> & {
    lingeringResult:
      | { ok: true; value: number[] }
      | { ok: false; err: unknown }
      | null
  }
): UseAccountDragAndDropReturn {
  // Drag and drop state
  const [draggedAccountId, setDraggedAccountId] = useState<number | null>(null)
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(null)

  const accounts =
    accountsFetch.lingeringResult?.ok === true
      ? accountsFetch.lingeringResult.value
      : null

  const saveOrder = (newAccounts: number[]) => {
    try {
      // Update backend with new order
      BackendRemote.rpc.setAccountsOrder(newAccounts).then(() => {
        // Refresh to get the updated order from backend
        accountsFetch.refresh()
      })
    } catch (_error) {
      // Revert on error
      accountsFetch.refresh()
    }
  }

  const handleDragStart = (e: React.DragEvent, accountId: number) => {
    e.dataTransfer.effectAllowed = 'move'
    // The browser snapshots the drag image right after this handler returns,
    // so hide the active/hover indicator for the snapshot only
    // and restore it on the next tick.
    const style = (e.currentTarget as HTMLElement).style
    style.setProperty('--als-active-indicator-color', 'transparent')
    setTimeout(() => style.removeProperty('--als-active-indicator-color'), 0)
    setDraggedAccountId(accountId)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedAccountId === null) return

    const target = e.currentTarget as HTMLDivElement
    const rect = target.getBoundingClientRect()
    const position = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom'

    setDropIndicator({ index, position })
  }

  const handleDragLeave = () => {
    setDropIndicator(null)
  }

  const handleDragEnd = () => {
    setDraggedAccountId(null)
    setDropIndicator(null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()

    if (draggedAccountId === null || dropIndicator === null) return
    if (!accounts) return

    const newAccounts = reorderedAccounts(
      accounts,
      accounts.indexOf(draggedAccountId),
      dropIndicator.index,
      dropIndicator.position
    )
    if (newAccounts) {
      saveOrder(newAccounts)
    }

    setDraggedAccountId(null)
    setDropIndicator(null)
  }

  // --- Reordering with a finger ---------------------------------------
  //
  // A finger never starts the browser's own drag and drop, so this gesture
  // is driven from touch events instead. It has to share one press with two
  // other gestures: moving right away scrolls the profile list, and pressing
  // without moving opens the profile's context menu. So the press arms a
  // reorder after ARM_DELAY_MS — before the browser's long press would fire
  // `contextmenu` — and only movement after that point takes the gesture
  // over from the scroller. Move earlier and it stays a scroll; don't move
  // at all and the context menu opens as it always did.
  //
  // Taking the gesture over means calling `preventDefault()` on `touchmove`,
  // which React's own (passive) listeners cannot do — hence the handlers
  // attached by hand for the duration of the press.

  const gesture = useRef<{
    identifier: number
    accountId: number
    startX: number
    startY: number
    armTimer: ReturnType<typeof setTimeout>
    armed: boolean
  } | null>(null)
  // The listeners of the running press, kept so that they are removed by
  // identity: the press ends in a later render than the one that started it.
  const listeners = useRef<null | (() => void)>(null)
  // Read at drop time, when the closure that has to write the new order is
  // older than the last indicator we rendered.
  const latestIndicator = useRef<DropIndicator | null>(null)

  const [touchDraggedAccountId, setTouchDraggedAccountId] = useState<
    number | null
  >(null)

  const showIndicator = (indicator: DropIndicator | null) => {
    latestIndicator.current = indicator
    setDropIndicator(indicator)
  }

  /**
   * Where the profile would land if the finger let go at `y`: above or below
   * whichever profile's middle is closest. Nearest-middle rather than
   * "the item under the finger" so that the gaps between the avatars, and
   * the space above the first and below the last one, all still aim
   * somewhere.
   */
  const indicatorAt = (y: number): DropIndicator | null => {
    if (!accounts) return null

    let closest: DropIndicator | null = null
    let closestDistance = Number.POSITIVE_INFINITY

    const items = document.querySelectorAll('[x-account-sidebar-account-id]')
    for (const item of Array.from(items)) {
      const accountId = Number(
        item.getAttribute('x-account-sidebar-account-id')
      )
      const index = accounts.indexOf(accountId)
      if (index < 0) continue

      const rect = item.getBoundingClientRect()
      const middle = rect.top + rect.height / 2
      const distance = Math.abs(y - middle)
      if (distance < closestDistance) {
        closestDistance = distance
        closest = { index, position: y < middle ? 'top' : 'bottom' }
      }
    }

    return closest
  }

  const endTouchGesture = (drop: boolean) => {
    const current = gesture.current
    if (!current) return

    clearTimeout(current.armTimer)
    listeners.current?.()
    listeners.current = null
    gesture.current = null

    const indicator = latestIndicator.current
    if (drop && current.armed && indicator && accounts) {
      const newAccounts = reorderedAccounts(
        accounts,
        accounts.indexOf(current.accountId),
        indicator.index,
        indicator.position
      )
      if (newAccounts) {
        saveOrder(newAccounts)
      }
    }

    setTouchDraggedAccountId(null)
    showIndicator(null)
  }

  const handleTouchStart = (e: React.TouchEvent, accountId: number) => {
    // A second finger during a reorder is not a reorder anymore.
    if (gesture.current || e.touches.length !== 1) {
      endTouchGesture(false)
      return
    }

    const touch = e.touches[0]
    const trackedTouch = (event: TouchEvent) =>
      Array.from(event.touches).find(
        candidate => candidate.identifier === touch.identifier
      )

    const onTouchMove = (event: TouchEvent) => {
      const current = gesture.current
      const moved = trackedTouch(event)
      if (!current || !moved) return

      if (!current.armed) {
        // Still deciding: movement this early means the list is being
        // scrolled, so hand the press back to the browser.
        if (
          Math.abs(moved.clientY - current.startY) > SLOP_PX ||
          Math.abs(moved.clientX - current.startX) > SLOP_PX
        ) {
          endTouchGesture(false)
        }
        return
      }

      // Ours now: no scrolling, no long press, no click afterwards.
      event.preventDefault()
      showIndicator(indicatorAt(moved.clientY))
    }

    const onTouchEnd = (event: TouchEvent) => {
      if (trackedTouch(event)) return
      endTouchGesture(true)
    }
    const onTouchCancel = () => endTouchGesture(false)
    // The finger rested instead of moving: the press was meant for the
    // context menu after all, so drop the "picked up" state again.
    const onContextMenu = () => endTouchGesture(false)

    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', onTouchEnd)
    document.addEventListener('touchcancel', onTouchCancel)
    document.addEventListener('contextmenu', onContextMenu)
    listeners.current = () => {
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
      document.removeEventListener('touchcancel', onTouchCancel)
      document.removeEventListener('contextmenu', onContextMenu)
    }

    gesture.current = {
      identifier: touch.identifier,
      accountId,
      startX: touch.clientX,
      startY: touch.clientY,
      armed: false,
      armTimer: setTimeout(() => {
        if (!gesture.current) return
        gesture.current.armed = true
        // Picked up: the profile dims, and the drop line follows the finger
        // from here on. If the finger never moves, the context menu opens on
        // top of this a moment later and `contextmenu` puts it back.
        setTouchDraggedAccountId(accountId)
        showIndicator(indicatorAt(touch.clientY))
      }, ARM_DELAY_MS),
    }
  }

  // Nothing above unmounts mid-press in practice, but a leaked listener (or
  // timer) would outlive the sidebar and keep calling into it.
  useEffect(
    () => () => {
      if (gesture.current) {
        clearTimeout(gesture.current.armTimer)
      }
      listeners.current?.()
    },
    []
  )

  return {
    draggedAccountId: draggedAccountId ?? touchDraggedAccountId,
    dropIndicator,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDragEnd,
    handleDrop,
    handleTouchStart,
  }
}
